import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {deleteRecord, fetchRecords} from '../services/PaymentIntentsService';
import {useState, useEffect} from 'react';
import {PaymentIntent} from '../types';
import {usePagination} from '../../../hooks/usePagination';
import {notifyError, notifySuccess} from '../../../components/ServiceNotifications';

export const usePaymentIntents = (customerId?: string) => {
    const queryClient = useQueryClient();
    const [keyword, setKeyword] = useState('');
    const pagination = usePagination();

    const filter = customerId ? {customerId} : {};
    const queryKey = customerId ? ['paymentIntents', {customerId}, pagination.currentToken] : ['paymentIntents', pagination.currentToken];

    const paymentIntentsQuery = useQuery({
        queryKey,
        queryFn: () => fetchRecords(filter, pagination.pageSize, pagination.currentToken),
    });

    useEffect(() => {
        if (paymentIntentsQuery.data) pagination.setNextToken(paymentIntentsQuery.data.nextToken);
    }, [paymentIntentsQuery.data]);

    useEffect(() => {
        pagination.resetPagination();
    }, [customerId]);

    const deleteMutation = useMutation({
        mutationFn: async (ids: string[]) => {
            const promises = ids.map(id => deleteRecord(id));
            return Promise.all(promises);
        },
        onSuccess: () => {
            notifySuccess('Payment intent deleted');
            queryClient.invalidateQueries({queryKey: ['paymentIntents']});
        },
        onError: notifyError,
    });

    const fetchRecordsAction = () => {
        queryClient.invalidateQueries({queryKey: ['paymentIntents']});
    };

    const deleteRecords = (ids?: string[]) => {
        if (ids && ids.length > 0) {
            deleteMutation.mutate(ids);
        }
    };

    const handleFilterByKeyword = (newKeyword: string) => {
        setKeyword(newKeyword);
    };

    const allItems: PaymentIntent[] = paymentIntentsQuery.data?.items || [];

    const visibleItems = keyword
        ? allItems.filter(item => {
            const search = keyword.toLowerCase();
            return (
                item.id?.toLowerCase().includes(search) ||
                item.customerId?.toLowerCase().includes(search) ||
                item.description?.toLowerCase().includes(search) ||
                item.status?.toLowerCase().includes(search)
            );
        })
        : allItems;

    return {
        items: allItems,
        visibleItems,
        loading: paymentIntentsQuery.isLoading,
        keyword,
        visibleCount: visibleItems.length,
        totalCount: allItems.length,
        fetchRecords: fetchRecordsAction,
        deleteRecords,
        handleFilterByKeyword,
        deleteMutation,
        currentPage: pagination.currentPage,
        hasNext: pagination.hasNext,
        hasPrevious: pagination.hasPrevious,
        goToNextPage: pagination.goToNextPage,
        goToPreviousPage: pagination.goToPreviousPage,
    };
};
